import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { DragEndEvent, DragOverlay, DragStartEvent, useDndMonitor } from '@dnd-kit/core';
import { useSelector } from 'react-redux';
import { selectContactsForIDs } from '@repo/redux/actions/contacts';
import { ContactAvatarGroup } from '@repo/components/avatars';
import styles from './DraggingUI.module.scss';
import { selectGroup } from '@repo/redux/actions/groups';
import { NavButton } from '@layouts/PrimaryLayout/Navigation/NavigationItem';
import { GroupIdentifierIcon } from '@repo/icons/GroupIdentifierIcon';

const DragContactIDs = ({ active }) => {
  const contactIDs = active.data.current.contactIDs;
  const contactsBeingDragged = useSelector(selectContactsForIDs(contactIDs));
  let label = `${contactIDs.size} Contacts`;

  if (contactsBeingDragged.length == 1) {
    label = contactsBeingDragged[0].displayName;
  }

  return (
    <div className={styles.contactIDDrag}>
      <ContactAvatarGroup contacts={contactsBeingDragged} displayCount={4} />
      <div className={styles.dragLabel}>{label}</div>
    </div>
  );
};

const DragGroup = ({ active }) => {
  const groupID = active.id;
  const group = useSelector(selectGroup(groupID));

  return (
    <NavButton
      className={styles.groupDrag}
      IconComponent={({ className }) => <GroupIdentifierIcon className={className} group={group} />}
      text={group.title}
    />
  );
};

const DragTypeComponentMap = {
  contactIDs: DragContactIDs,
  group: DragGroup,
};

export default function DraggingUI({
  onDragStart,
  onDragEnd,
}: {
  onDragStart?: (e: DragStartEvent) => void;
  onDragEnd?: (e: DragEndEvent) => void;
}) {
  const [currentlyDragging, setCurrentlyDragging] = useState(false);
  const [dragEventData, setDragEventData] = useState({ active: null });
  const portalNodeRef = useRef(null);
  const DragDisplay = DragTypeComponentMap[dragEventData.active?.data?.current?.type];

  useDndMonitor({
    onDragStart: (e) => {
      if (onDragStart) {
        onDragStart(e);
      }
      setCurrentlyDragging(true);
      setDragEventData(e);
    },
    onDragOver: (e) => setDragEventData(e),
    onDragEnd: (e) => {
      if (onDragEnd) {
        onDragEnd(e);
      }
      setCurrentlyDragging(false);
    },
    onDragCancel: () => setCurrentlyDragging(false),
  });

  useEffect(() => {
    portalNodeRef.current = document.querySelector('#dragAndDrop');
    if (!portalNodeRef.current) {
      console.warn('Could not find element #dragAndDrop');
    }
  }, []);

  return (
    <>
      {portalNodeRef.current &&
        createPortal(
          <div className={cn(styles.dragOverlay, currentlyDragging && styles.dragging)}>
            <DragOverlay dropAnimation={null}>
              {currentlyDragging && DragDisplay ? (
                <div style={{ width: 30, height: 30, position: 'absolute' }}>
                  <DragDisplay active={dragEventData.active} />
                </div>
              ) : null}
            </DragOverlay>
          </div>,
          portalNodeRef.current,
        )}
    </>
  );
}
